import { QueryType, RuleCondition, SortOrder } from "capitalrx-types";

import { BenefitUpdateDatesDataSection } from "../updateDates";

export interface MedicalBenefit {
  readonly auto_adjudication_rules?: ReadonlyArray<Rule>;
  readonly id: number;
  readonly medical_plan_benefit_id?: number;
  readonly benefit_id?: string | null;
  readonly created_at?: string | null;
  readonly deleted_at?: string | null;
  readonly start_date?: string;
  readonly end_date?: string;
  readonly plan_id?: number;
  readonly pend_rules?: ReadonlyArray<Rule>;
  readonly effective_date?: string | null;
  readonly termination_date?: string | null;
  readonly benefit_type?: string;
  readonly name?: string;
  readonly identity?: string;
  readonly accumulations?: Accumulations;
  readonly networks?: Networks;
  readonly prior_authorizations?: PriorAuthorizations;
  readonly standard_cost_shares?: StandardCostShares;
  readonly preventive?: PreventiveServices;
  readonly services?: Services;
  readonly pharmacy_coverage_strategy_id?: Array<string>;
  readonly claim_validation_checks?: Array<ValidationCheck>;
  readonly service_maximums?: ServiceMaximumsConfig;
  readonly member_submitted_claims?: MemberSubmittedClaims;
  readonly coordination_of_benefits?: CoordinationOfBenefits;
  readonly adjudication_configurations?: unknown;
}

export interface MedicalBenefitDraftListResponse {
  readonly results: ReadonlyArray<MedicalBenefitDraftListResult>;
  readonly result_count: string;
}

type serviceMaximumsMaximumType = "visits" | "days";
export type serviceMaximumsActionOnceMaximumsReached = "pend" | "deny";

export interface ServiceMaximums {
  readonly maximum_applies_to: Array<string>;
  readonly maximum_applies_at: Array<string>;
  readonly maximum_type: serviceMaximumsMaximumType;
  readonly maximum_number: string;
  readonly action_once_maximum_is_reached: serviceMaximumsActionOnceMaximumsReached;
  readonly unit_type: string;
}

export interface ServiceMaximumsConfig {
  readonly service_maximums_reset_month?: number;
  readonly service_maximums_reset_day?: number;
  readonly maximums?: Array<ServiceMaximums>;
}

export interface ValidationCheck {
  readonly type: string;
  readonly threshold: string;
  readonly action: string;
}

export interface Accumulations {
  readonly accumulations_reset_day?: number;
  readonly accumulations_reset_month?: number;
  readonly accumulators?: Array<AccumulatorData>;
  readonly coverage_types?: Array<CoverageTypeData>;
  readonly plan_maximum_settings?: PlanMaximumSetting;
}

export interface Networks {
  readonly networks: ReadonlyArray<{ network: string; network_type: string }>;
  readonly notes?: string;
}

type LackOfCoverAgePenaltyType = "no_coverage" | "payment_to_provider_reduced" | "member_cost_share_increased";

export interface PriorAuthorizationData {
  readonly reviews_conducted_by?: string;
  readonly decision_appeals_reviewed_by?: string;
  readonly lack_of_prior_authorization_penalty_type?: LackOfCoverAgePenaltyType;
  readonly lack_of_prior_authorization_penalty_amount?: string;
  readonly lack_of_prior_authorization_penalty_amount_type?: "dollars" | "percent";
}

export interface PriorAuthorizations {
  readonly [key: string]: PriorAuthorizationData;
}

export interface StandardCostShareIdentifier {
  readonly identifier: string;
  readonly operator: string;
  readonly values: Array<string>;
}

export interface StandardCostShareData {
  readonly visit_type: string;
  readonly identifier_logic: StandardCostShareIdentifier;
  readonly coinsurance_percent?: string;
  readonly copay_amount?: string;
  readonly cost_share_comparison?: string;
  readonly cost_share_minimum?: string;
  readonly cost_share_maximum?: string;
  readonly cost_share_per?: string;
}

export interface StandardCostShares {
  [key: string]: Array<StandardCostShareData>;
}

export type StandardCostShareField = StandardCostShareData & { id?: string };

export interface ServiceLimit {
  type: string;
  covered_after_event?: Array<string>;
  covered_after_event_exact_codes?: Array<string>;
  covered_after_event_wildcard_codes?: string; // comma-separated
  covered_after_event_lookback_years?: string;
  covered_when_diagnosed_exact_codes?: Array<string>;
  covered_when_diagnosed_wildcard_codes?: string; // comma-separated
  covered_when_diagnosed_lookback_years?: string;
  covered_when_received_at?: Array<string>;
  age_minimum?: string;
  age_maximum?: string;
  gender_female?: boolean;
  gender_male?: boolean;
  gender_other?: boolean;
  relationship_code?: Array<string>;
  maximum_services_every_count?: string;
  maximum_services_every_units?: string;
  maximum_services_total?: string;
  maximum_services_total_units?: string;
  maximum_amount_covered_timeframe?: string;
  maximum_amount_covered_total?: string;
  unit?: string;
  unit_type?: string;
  requires_prior_authorization?: boolean;
}

export interface ServiceCostShare {
  coinsurance_percent?: string;
  copay_amount?: string;
  cost_share_comparison?: string;
  cost_share_maximum?: string;
  cost_share_minimum?: string;
}

export interface ServiceCostShareVariable extends ServiceCostShare {
  cost_share_per_time_period?: string;
  cost_share_range_from?: string;
  cost_share_range_to?: string;
}

export interface ServiceCostShareSetting {
  cost_share_type: string;
  cost_share_per: string;
  place_of_service: Array<string>;
  cost_share_data: ReadonlyArray<ServiceCostShare | ServiceCostShareVariable>;
  accumulators?: ReadonlyArray<string>;
}

export interface ServiceAttributes {
  coverage?: string;
  cost_shares?: ReadonlyArray<ServiceCostShareSetting>;
  limits?: Array<ServiceLimit>;
}

export interface ServiceCategory {
  name?: string;
  service_category: string;
  preventive_category?: string; // jank because of a union type
  medical_service_configuration_id: number; // adjudication.medical_service_configurations.id
  medical_benefit_service_category_id: number; // adjudication.medical_benefit_service_categories.id
  attributes: ServiceAttributes;
}

export interface PreventiveCategory {
  name?: string;
  service_category?: string; // jank because of a union type
  preventive_category: string;
  medical_preventive_configuration_id: number; // adjudication.medical_preventive_configuration.id
  medical_benefit_preventive_category_id: number; // adjudication.medical_benefit_preventive_categories.id
  attributes: ServiceAttributes;
}

export type ServiceAttributesFormFields = Omit<ServiceAttributes, "limits">
  & Omit<ServiceLimit, "type"> & {
    limit_type?: string;
  };

export interface ServicesData {
  [service: string]: ServiceCategory | PreventiveCategory;
}

/**
 * service legacy types
 */
export interface ServiceAttributesLegacy {
  coverage?: string;
  cost_share?: string;
  custom_cost_share_dollar_amount?: string;
  custom_cost_share_logic?: string;
  custom_cost_share_maximum?: string;
  custom_cost_share_minimum?: string;
  custom_cost_share_percentage_amount?: string;
  limits?: Array<ServiceLimit>;
}

export type ServiceAttributesLegacyFormFields = Omit<ServiceAttributesLegacy, "limits">
  & Omit<ServiceLimit, "type"> & {
    limit_type?: string;
  };

export interface ServiceCategoryNested {
  name: string;
  attributes: ServiceAttributesLegacy;
  children?: ReadonlyArray<ServiceCategoryNested>;
  parent: string | null;
  isExpanded?: boolean;
  serviceId: string;
  serviceIdDepth: number;
}

export interface ServicesDataLegacy {
  [service: string]: ServiceCategoryNested;
}

export type ServicesForm = {
  medical_service_configuration_id?: number; // adjudication.medical_service_configurations.id
};

export type Services = {
  [network: string]: ServicesData | ServicesDataLegacy;
} & ServicesForm;

export type PreventiveServicesForm = {
  medical_preventive_configuration_id?: number; // adjudication.medical_preventive_configuration.id
};

export type PreventiveServices = {
  [network: string]: ServicesData | ServicesDataLegacy;
} & PreventiveServicesForm;

export interface MemberSubmittedClaimData {
  readonly allow_member_submitted_claims: "allowed" | "not_allowed";
  readonly reimbursement_rate: string;
  readonly number_of_days_to_file_claim: string;
  readonly notes?: string;
}

export interface MemberSubmittedClaims {
  [network: string]: MemberSubmittedClaimData;
}

type coordinationOfBenefitsMethodType = "integrated_benefit" | "traditional";
type excludeCostsType = "copay" | "coinsurance" | "deductible";
export type LineItemWithPrimaryPayerOutcome = "balance_line_item_to_primary" | "balance_line_item_to_secondary";

export interface LineItemWithPrimaryPayer {
  readonly paid?: LineItemWithPrimaryPayerOutcome;
  readonly denied?: LineItemWithPrimaryPayerOutcome;
}

export interface LineItemWithPrimaryPayerByNetwork {
  readonly in_network?: LineItemWithPrimaryPayer;
  readonly out_of_network?: LineItemWithPrimaryPayer;
}

export interface CoordinationOfBenefits {
  readonly allow_secondary_claims_processing?: boolean;
  readonly method?: coordinationOfBenefitsMethodType;
  readonly member_cost_sharing_applies?: boolean;
  readonly excluded_costs?: ReadonlyArray<excludeCostsType>;
  readonly line_item_with_primary_payer?: LineItemWithPrimaryPayerByNetwork;
  readonly notes?: string;
}

export interface MedicalBenefitListResult {
  readonly plan_benefit: MedicalBenefit;
}

export interface MedicalBenefitDraftListResult {
  readonly plan_benefit_draft: MedicalBenefit;
}

export interface MedicalBenefitListResponse {
  readonly results: ReadonlyArray<MedicalBenefitListResult>;
  readonly result_count: string;
}

export interface OrderByOptions {
  readonly id?: SortOrder;
  readonly benefit_id?: SortOrder;
  readonly plan_id?: SortOrder;
  readonly created_at?: SortOrder;
  readonly deleted_at?: SortOrder;
  readonly effective_date?: SortOrder;
  readonly termination_date?: SortOrder;
  readonly benefit_type?: SortOrder;
  readonly name?: SortOrder;
}

export interface MedicalBenefitListOptions {
  readonly plan_id?: string | number;
  readonly benefit_id?: string;
  readonly benefit_ids?: ReadonlyArray<string>;
  readonly only_fields?: ReadonlyArray<string>;
  readonly group_by_benefit_id?: boolean;
  readonly exact_benefit_id_match?: boolean;
  readonly order_by?: OrderByOptions[];
  readonly results_per_page?: number;
  readonly page_number?: number;
  readonly $query?: QueryType;
}

export interface MedicalBenefitUpdateDatesData {
  readonly dates: ReadonlyArray<BenefitUpdateDatesDataSection>;
  readonly benefit_id: string;
}

type CoverageTypeOption =
  | "subscriber_only"
  | "subscriber_plus_one"
  | "subscriber_plus_spouse"
  | "subscriber_plus_child"
  | "subscriber_plus_dependent"
  | "family";

type CoverageTypeCode = "01" | "02" | "03" | "04" | "05" | "06" | "07";

export interface CoverageTypeData {
  readonly coverage_type: CoverageTypeOption;
  readonly coverage_type_codes: ReadonlyArray<CoverageTypeCode>;
}

type AccumulationType = "deductible" | "maximum_out_of_pocket" | "plan_maximum" | "individual_maximum" | "custom";
type ActionId = number;
type AccumulatorName = string;
type DoesNotApplyStaticOption = "preventive" | "telehealth";

export interface CoverageAmountData {
  readonly individual_amount: string;
  readonly family_amount: string;
  readonly coverage_type: CoverageTypeOption;
}

export interface OtherAccumulatorIdentifier {
  network_type: string;
  accumulator_name: AccumulatorName;
}

export interface AccumulatorData {
  readonly type: AccumulationType;
  readonly name: AccumulatorName;
  readonly network_type: string;
  readonly is_combined_with_pharmacy: boolean;
  readonly isNameEditable?: boolean;
  readonly pharmacy_accumulator: string;
  readonly does_not_apply_to: Array<DoesNotApplyStaticOption | ActionId>;
  readonly only_applies_to_actions: Array<ActionId>;
  readonly coverage_amounts: Array<CoverageAmountData>;
  readonly spend_also_goes_toward: Array<AccumulatorName | OtherAccumulatorIdentifier>; // stored as AccumulatorName for form
  readonly carryover_months?: string;
  // plan max
  readonly duration?: "years" | "lifetime";
}

export interface PlanMaximumSetting {
  readonly member_percentage_on_straddle?: string;
  readonly action_once_maximum_is_reached: "deny" | "member_pays_percentage";
  readonly member_percentage_once_maximum_reached?: string;
}

export type ExtendedAccumulatorData = AccumulatorData & {
  readonly isNameEditable?: boolean;
};

export enum MEDICAL_RULE_SELECTIONS {
  all_claims = "All Claims",
  preventive_categories = "All Claim Preventive Categories",
  rendering_provider_cms_specialty_codes = "All Claim Rendering Provider CMS Specialty Codes",
  rendering_provider_npis = "All Claim Rendering Provider NPIs",
  service_categories = "All Claim Service Categories",
  any_diagnosis_code = "Any Diagnosis Code",
  any_procedure_code = "Any Procedure Code",
  any_related_causes_code = "Any Related Causes Code",
  any_remark_code = "Any Remark Code",
  any_revenue_code = "Any Revenue Code",
  bill_type_code = "Bill Type Code",
  billing_provider_npi = "Billing Provider NPI",
  billing_provider_tin = "Billing Provider TIN",
  claim_type = "Claim Type",
  is_direct_member_reimbursement = "DMR Claim",
  minimum_service_date = "Minimum Service Date",
  last_processed_by = "Last Processed By",
  place_of_service = "Place of Service Code",
  plan_pay_amount = "Plan Pay Amount",
}

export type MedicalRuleSelectionTypes = keyof typeof MEDICAL_RULE_SELECTIONS;

export interface Rule {
  readonly name: string;
  readonly definitions: ReadonlyArray<ReadonlyArray<RuleCondition<MedicalRuleSelectionTypes>>>;
  readonly description: string;
}
